import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { agoIso, duration, fmtTime } from "../format";
import { navigate, useRoute } from "../route";
import type { TaskEnv, TaskRun, TaskRunsPage } from "../types";

/**
 * The durable task queue, per stack.
 *
 * Answers "did that background work actually run?" — the question Starlette
 * `BackgroundTasks` could never answer, since they died with the process that
 * created them and left no record. Every row here is a `task_runs` document.
 *
 * Two things to keep in mind while reading it, both inherited from the engine
 * (see `bean_engine/storage/task_runs.py`):
 *
 *  * **The status is what a worker last believed, not ground truth.** A worker
 *    killed by SIGKILL never writes its final status, so a row can sit at
 *    `running` forever while the work itself was redelivered and completed. The
 *    guarantee lives in the un-acked Pub/Sub message, not in this record.
 *  * **This is a window, not the collection.** Filtering happens inside the
 *    newest N runs, so an empty `failed` view means "none in the window", which
 *    is why the footer says how wide the window was.
 *
 * Read-only on purpose. A retry button would publish a second message for work
 * whose first message may still be in flight, which is how you get a task
 * running twice.
 */

/** Stacks, in the order the toggle shows them. Prod first — it is the one you
 *  are usually being paged about. */
const ENVS: { key: TaskEnv; label: string }[] = [
  { key: "production", label: "Production" },
  { key: "development", label: "Dev" },
];

/**
 * Status filters. `all` first, then the two that mean "something is wrong",
 * then the quiet ones — an operator opens this view because of the middle group.
 */
const STATUSES = [
  "all",
  "failed",
  "retrying",
  "interrupted",
  "running",
  "queued",
  "completed",
  "skipped",
] as const;

type StatusFilter = (typeof STATUSES)[number];

/** A run in one of these is finished for good; anything else still has a worker
 *  or a redelivery coming, which is what makes the view worth polling. */
const TERMINAL = new Set(["completed", "failed", "skipped"]);

/** How often to re-poll while any loaded run is still in flight. Slower than
 *  the live-session view: tasks take seconds to minutes, not milliseconds. */
const POLL_MS = 5000;

/** Badge tone per status, reusing the ok/warn/bad outcome tints the rest of the
 *  app already uses. `running` is not an outcome, so it gets `live` — filled
 *  rather than tinted, which reads as "still happening" instead of a verdict. */
function tone(status: string): string {
  if (status === "failed") return "badge bad";
  if (status === "retrying" || status === "interrupted") return "badge warn";
  if (status === "running") return "badge live";
  if (status === "completed") return "badge ok";
  return "badge";
}

/** The queue stores args as a JSON string. Pretty-print when it parses, show the
 *  raw text when it does not — a payload that will not parse is itself the bug
 *  worth seeing, so it must never be swallowed. */
function prettyPayload(payload: string): string {
  if (!payload) return "—";
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

/** "2 / 3" — attempts spent against the ceiling. Bare numbers here are easy to
 *  misread as a count of failures. */
function tries(run: TaskRun): string {
  return `${run.num_tries ?? 0} / ${run.max_tries ?? 0}`;
}

function TaskDetail({ run }: { run: TaskRun }) {
  return (
    <tr className="detail">
      <td colSpan={6}>
        <dl className="kv">
          <dt>Task id</dt>
          <dd>
            <code>{run.task_id}</code>
          </dd>
          <dt>Idempotency key</dt>
          <dd>
            <code className="muted small">{run.idempotency_key || "—"}</code>
          </dd>
          <dt>Queue</dt>
          <dd className="small">
            {run.queue || "default"}
            {run.unique && <span className="badge"> unique</span>}
            {run.debounced && <span className="badge"> debounced</span>}
          </dd>
          <dt>Worker</dt>
          <dd className="small">
            {/* "inline" means the engine ran it in-process because no topic was
                configured — durable in name only, and expected in dev alone. */}
            {run.worker_id === "inline" ? (
              <span className="badge warn" title="Ran in-process, not on the worker pool">
                inline
              </span>
            ) : (
              <code className="muted">{run.worker_id || "—"}</code>
            )}
          </dd>
          <dt>Enqueued</dt>
          <dd className="small">{fmtTime(run.enqueued_at ?? undefined) || "—"}</dd>
          {run.scheduled_after && (
            <>
              <dt>Scheduled after</dt>
              <dd className="small">{fmtTime(run.scheduled_after)}</dd>
            </>
          )}
          <dt>Last update</dt>
          <dd className="small">{fmtTime(run.updated_at ?? undefined) || "—"}</dd>
          {run.completed_at && (
            <>
              <dt>Finished</dt>
              <dd className="small">
                {fmtTime(run.completed_at)}
                {duration(run.enqueued_at ?? undefined, run.completed_at) && (
                  <span className="muted">
                    {" "}
                    · {duration(run.enqueued_at ?? undefined, run.completed_at)} end to end
                  </span>
                )}
              </dd>
            </>
          )}
        </dl>

        {run.error && (
          <>
            <div className="muted small">
              {/* `skipped` puts its reason in the same field, and a skip is not
                  a failure — labelling both "Error" would misread the row. */}
              {run.status === "skipped" ? "Reason" : "Error"}
            </div>
            <pre
              className="small"
              style={{ whiteSpace: "pre-wrap", margin: "0.2rem 0 0.6rem" }}
            >
              {run.error}
            </pre>
          </>
        )}

        <div className="muted small">Payload</div>
        <pre
          className="small"
          style={{ whiteSpace: "pre-wrap", margin: "0.2rem 0 0" }}
        >
          {prettyPayload(run.payload)}
        </pre>
      </td>
    </tr>
  );
}

export function Tasks() {
  const route = useRoute();
  const env = route.env;

  const [page, setPage] = useState<TaskRunsPage | null>(null);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      if (!opts.quiet) setLoading(true);
      try {
        const next = await api.listTasks(env, {
          status: status === "all" ? "" : status,
        });
        setPage(next);
        setError("");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [env, status],
  );

  // Switching stack or filter is a fresh question — drop the open row with it,
  // since the id it points at may not be in the new result at all.
  useEffect(() => {
    setOpen("");
    void load();
  }, [load]);

  // Poll only while something is actually in flight, so a view left open on a
  // quiet queue costs nothing and stops on its own.
  const inFlight = useMemo(
    () => (page?.items ?? []).some((r) => !TERMINAL.has(r.status)),
    [page],
  );
  useEffect(() => {
    if (!inFlight) return;
    const id = setInterval(() => void load({ quiet: true }), POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, load]);

  // Name search is client-side over the loaded page. The server takes a `name`
  // filter too, but it is an exact match — this is for "show me the recap ones"
  // when you cannot remember the registered name exactly.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return page?.items ?? [];
    return (page?.items ?? []).filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.task_id.toLowerCase().includes(q) ||
        (r.idempotency_key ?? "").toLowerCase().includes(q),
    );
  }, [page, query]);

  const counts = page?.counts ?? {};
  const scanned = page?.scanned ?? 0;

  return (
    <section>
      <header className="section-head">
        <h2>Tasks</h2>
        <button className="ghost" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      <p className="muted small">
        Background work handed to the durable queue. Dev and prod share one
        Firestore database and are kept apart by collection, so this reads either
        stack from the same place.
      </p>

      {/* Stack first, filter second: which queue you are looking at changes the
          meaning of every number below it. Routed, so the choice is linkable. */}
      <div className="tabs">
        {ENVS.map((e) => (
          <button
            key={e.key}
            className={env === e.key ? "tab active" : "tab"}
            onClick={() => navigate({ tab: "tasks", env: e.key })}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="tabs">
        {STATUSES.map((s) => (
          <button
            key={s}
            className={status === s ? "tab active" : "tab"}
            onClick={() => setStatus(s)}
          >
            {s === "all" ? "All" : s}
            <span className="count">
              {s === "all" ? scanned : (counts[s] ?? 0)}
            </span>
          </button>
        ))}
      </div>

      <div className="add-admin">
        <input
          type="search"
          placeholder="Filter by task name, task id, or idempotency key"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!loading && visible.length === 0 && (
        <p className="muted empty">
          {query
            ? "No run matches that."
            : status !== "all"
              ? `No ${status} runs among the newest ${scanned}.`
              : `No tasks have run on ${env === "production" ? "production" : "dev"} yet.`}
        </p>
      )}

      {visible.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Enqueued ▾</th>
                <th>Task</th>
                <th>Status</th>
                <th>Tries</th>
                <th>Took</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((run) => {
                const isOpen = open === run.task_id;
                return [
                  <tr
                    key={run.task_id}
                    className={run.status === "failed" ? "row-error" : undefined}
                  >
                    <td className="nowrap muted">{agoIso(run.enqueued_at ?? undefined)}</td>
                    <td>
                      <code>{run.name}</code>
                      <div className="muted small">{run.task_id}</div>
                    </td>
                    <td className="nowrap">
                      <span className={tone(run.status)}>{run.status}</span>
                    </td>
                    <td className="nowrap muted small">{tries(run)}</td>
                    <td className="nowrap muted small">
                      {duration(
                        run.enqueued_at ?? undefined,
                        run.completed_at ?? undefined,
                      ) || "—"}
                    </td>
                    <td>
                      <button
                        className="ghost small"
                        onClick={() => setOpen(isOpen ? "" : run.task_id)}
                      >
                        {isOpen ? "Hide" : "Open"}
                      </button>
                    </td>
                  </tr>,
                  isOpen ? <TaskDetail key={`${run.task_id}-detail`} run={run} /> : null,
                ];
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small">
        {query
          ? `${visible.length} of ${page?.items.length ?? 0} shown`
          : `${visible.length} shown`}
        {" · "}
        {/* The bound is stated rather than implied: "no failures" is only
            meaningful alongside how far back the view actually looked. */}
        {page?.complete
          ? `the whole ${page.collection} collection (${scanned} runs)`
          : `the newest ${scanned} runs in ${page?.collection ?? "the collection"} — older ones were not read`}
        {inFlight && " · refreshing while work is in flight"}
      </p>
    </section>
  );
}
